import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, PhoneCall, HeartHandshake, Hourglass } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (k) => { const [y, m] = k.split("-"); return `${MONTHS[+m - 1]} ${y}`; };
const ukDate = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleDateString("en-GB"); };

const Stat = ({ id, icon: Icon, label, value, sub }) => (
  <div className={`${CARD} p-4`} data-testid={`ft-stat-${id}`}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}><Icon className="h-4 w-4" style={{ color: "var(--sj-primary)" }} /> {label}</div>
    <div className="text-3xl font-extrabold font-head mt-1" style={{ color: "var(--sj-primary)" }} data-testid={`ft-stat-${id}-value`}>{value}</div>
    {sub && <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{sub}</div>}
  </div>
);

export const FirstTimersReport = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/care/first-timers-report", { params: { months } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [months]);
  if (!data) return <div className={`${CARD} p-12 text-center`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>;
  const t = data.totals;
  const max = Math.max(1, ...data.months.map((m) => m.first_timers));
  return (
    <div className="space-y-5" data-testid="first-timers-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>First-time visitors from the Connect form or added by staff, and whether pastoral care reached them (spoke, met or got a reply) within 7 days.</p>
        <select data-testid="ft-months" value={months} onChange={(e) => setMonths(+e.target.value)} className="bg-white border border-[#EBE9E4] rounded-xl px-3 py-2 text-sm">
          {[3, 6, 12, 24].map((n) => <option key={n} value={n}>Last {n} months</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat id="total" icon={Sparkles} label="First-timers" value={t.first_timers} />
        <Stat id="reached" icon={HeartHandshake} label="Reached in 7 days" value={`${t.reached_rate}%`} sub={`${t.reached_7d} of ${t.first_timers}`} />
        <Stat id="contacted" icon={PhoneCall} label="Contacted in 7 days" value={t.contacted_7d} sub="Any call, text or visit logged" />
        <Stat id="not-yet" icon={Hourglass} label="Not contacted yet" value={t.not_yet} />
      </div>
      <div className={`${CARD} p-5`} data-testid="ft-monthly">
        <h3 className="font-head text-lg font-semibold mb-4">By month</h3>
        <div className="space-y-2.5">
          {data.months.map((m) => (
            <div key={m.month} data-testid={`ft-month-${m.month}`} className="grid grid-cols-[80px_1fr_150px] items-center gap-3 text-sm">
              <span className="font-medium">{monthLabel(m.month)}</span>
              <div className="h-6 rounded-full overflow-hidden relative" style={{ background: "#F1F4F2" }}>
                <div className="h-full rounded-full absolute left-0 top-0" style={{ width: `${(m.first_timers / max) * 100}%`, background: "#CFE3DA" }} />
                <div className="h-full rounded-full absolute left-0 top-0" style={{ width: `${(m.reached_7d / max) * 100}%`, background: "var(--sj-primary)" }} />
              </div>
              <span className="text-xs" style={{ color: "var(--sj-text-2)" }}><b>{m.first_timers}</b> visitors · <b style={{ color: "var(--sj-primary)" }}>{m.reached_7d}</b> reached in 7d</span>
            </div>
          ))}
        </div>
        <div className="flex gap-4 mt-4 text-xs" style={{ color: "var(--sj-text-3)" }}>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: "#CFE3DA" }} /> First-timers</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: "var(--sj-primary)" }} /> Reached within a week</span>
        </div>
      </div>
      <div className={`${CARD} p-5 overflow-x-auto`} data-testid="ft-people">
        <h3 className="font-head text-lg font-semibold mb-3">First-time visitors</h3>
        {data.people.length === 0 ? <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No first-time visitors in this period yet.</p> : (
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}><th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Visited</th><th className="py-2 pr-3">First contact</th><th className="py-2">Reached</th></tr></thead>
            <tbody>{data.people.map((p) => (
              <tr key={p.id} className="border-t" style={{ borderColor: "var(--sj-border)" }} data-testid={`ft-person-${p.id}`}>
                <td className="py-2.5 pr-3 font-medium">{p.name}</td>
                <td className="py-2.5 pr-3">{ukDate(p.visited_at)}</td>
                <td className="py-2.5 pr-3">{ukDate(p.first_contact_at)}</td>
                <td className="py-2.5">{p.reached_at ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={p.reached_within_week ? { background: "#E3F1EC", color: "var(--sj-primary)" } : { background: "#FBEEE6", color: "#B45309" }}>{p.reached_within_week ? "Within a week" : "After a week"} · {ukDate(p.reached_at)}</span> : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FDECEC", color: "#B42318" }}>Not yet</span>}</td>
              </tr>))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
